import './ProjectEdit.scss';

import { solid } from '@fortawesome/fontawesome-svg-core/import.macro'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useMemo } from 'react'
import { useEffect } from 'react';
import { useState } from 'react';
import { Alert, Card, Container, Row, Col, Breadcrumb, Badge, Modal, Button, Form } from 'react-bootstrap'
import { Helmet } from 'react-helmet'
import { Link, useParams } from 'react-router-dom'
import { Projects } from '../../api/Projects';
import AlidaLoader from '../../components/common/AlidaLoader';
import { Applications } from '../../api/Applications';
import { useToasts } from '../../contexts/toasts';
import { Flex, Pagination, Select, Tabs, Tooltip } from 'antd';
import { Datasets } from '../../api/Datasets';
import { Models } from '../../api/Models';
import { useFullScreenLoader } from '../../contexts/fullScreenLoader';
import AppSearch from '../../components/common/AppSearch';
import { Services } from '../../api/Services';
import ServiceItem from '../../components/list-items/ServiceItem';
import { ProjectAsset } from '../../components/list-items/ProjectAsset';
import BdaApplicationItem from '../../components/list-items/BdaApplicationItem';
import AssetItem from '../../components/list-items/AssetItem';
import { memo } from 'react';
import { debounce } from 'lodash';
import { accessLevelOptions, setObjectType } from '../../utils';
import { Project } from '../../models/Project';
import { BdaApplication } from '../../models/BdaApplication';
import { BdaDataset } from '../../models/BdaDataset';
import { BdaModel } from '../../models/BdaModel';
import { BdaService } from '../../models/BdaService';
import { PagedResults } from '../../models/generics';
import { useSelector } from 'react-redux';
import { sectionLabelsSelector } from '../../slices/sectionsSlice';
import { RootState } from '../../store';
import AppLoader from '../../components/common/AppLoader';

export default function ProjectEdit() {
  const { id } = useParams();
  const [project, setProject] = useState<Project>();
  const [newProject, setNewProject] = useState<Project>();

  const [loading, setLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [validated, setValidated] = useState(false);
  const [error, setError] = useState(false);

  const [currentAppsPage, setCurrentAppsPage] = useState(1);
  const [currentDatasetsPage, setCurrentDatasetsPage] = useState(1);
  const [currentModelsPage, setCurrentModelsPage] = useState(1);
  const [currentServicesPage, setCurrentServicesPage] = useState(1);
  const [appSearch, setAppSearch] = useState("");
  const [datasetSearch, setDatasetSearch] = useState("");
  const [modelSearch, setModelSearch] = useState("");
  const [serviceSearch, setServiceSearch] = useState("");

  const [applications, setApplications] = useState<BdaApplication[]>();
  const [datasets, setDatasets] = useState<BdaDataset[]>();
  const [models, setModels] = useState<BdaModel[]>();
  const [services, setServices] = useState<BdaService[]>();
  const [totalApplications, setTotalApplications] = useState<number>();
  const [totalDatasets, setTotalDatasets] = useState<number>();
  const [totalModels, setTotalModels] = useState<number>();
  const [totalServices, setTotalServices] = useState<number>();

  const [assetToRemove, setAssetToRemove] = useState<any>();
  const [showRemoveModal, setShowRemoveModal] = useState(false);

  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();
  const serviceLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "services"));
  const datasetLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "datasets"));
  const modelLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "models"));
  const applicationLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "applications"));

  useEffect(() => {
    getProject();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (project) {
      setApplications(undefined);
      getApplications();
    }
    // eslint-disable-next-line
  }, [project, currentAppsPage, appSearch]);

  useEffect(() => {
    if (project) {
      setDatasets(undefined);
      getDatasets();
    }
    // eslint-disable-next-line
  }, [project, currentDatasetsPage, datasetSearch]);

  useEffect(() => {
    if (project) {
      setModels(undefined);
      getModels();
    }
    // eslint-disable-next-line
  }, [project, currentModelsPage, modelSearch]);

  useEffect(() => {
    if (project) {
      setServices(undefined);
      getServices();
    }
    // eslint-disable-next-line
  }, [project, currentServicesPage, serviceSearch]);

  const debounceAppSearch = useMemo(() => debounce(value => setAppSearch(value), 500), []);
  const debounceDatasetSearch = useMemo(() => debounce(value => setDatasetSearch(value), 500), []);
  const debounceModelSearch = useMemo(() => debounce(value => setModelSearch(value), 500), []);
  const debounceServiceSearch = useMemo(() => debounce(value => setServiceSearch(value), 500), []);

  useEffect(() => {
    return () => {
      debounceAppSearch.cancel();
      debounceDatasetSearch.cancel();
      debounceModelSearch.cancel();
      debounceServiceSearch.cancel();
    };
  }, [debounceAppSearch, debounceDatasetSearch, debounceModelSearch, debounceServiceSearch])

  const getProject = useCallback((refresh?: boolean) => {
    Projects.getProject(+id!)
      .then(
        ({ data: proj }) => {
          if (refresh) {
            setProject(p => ({
              ...p!,
              bdaApplications: proj.bdaApplications,
              datasets: proj.datasets,
              models: proj.models,
              services: proj.services,
            }));
            setLoading(false);
          }
          else {
            setProject(proj);
            setNewProject(proj);
          }
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('projects-error', message, `Projects Error ${status ? `(${status})` : ''}`, 'danger');
          setError(true);
        }
      )
  }, [id, showToast]);

  const refreshAssets = useCallback(() => {
    setLoading(true);
    getProject(true);
  }, [getProject]);

  const getDatasets = useCallback(() => {
    const params = new URLSearchParams();
    // params.set("evaluation", false);
    params.set("page", `${currentDatasetsPage}`);
    params.set("size", `${20}`);
    params.set("sort", "id");
    params.set("desc", `${true}`);
    if (datasetSearch.length) params.set("freeText", datasetSearch);
    if (project!.datasets?.length) params.set("excludeIds", project!.datasets.map(d => d.id).join(","));

    Datasets.getDatasets(params)
      .then(
        ({ data: datasetsPages }) => {
          setDatasets((datasetsPages as PagedResults<BdaDataset>).content);
          setTotalDatasets((datasetsPages as PagedResults<BdaDataset>).pageMetadata.totalElements);
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('datasets-error', message, `${datasetLabels.plural} Error ${status ? `(${status})` : ''}`, 'danger');
          setDatasets([]);
          // setError(true);
        }
      );
  }, [currentDatasetsPage, datasetSearch, datasetLabels, project, showToast]);

  const getModels = useCallback(() => {
    const params = new URLSearchParams();
    // params.set("evaluation", false);
    params.set("page", `${currentModelsPage}`);
    params.set("size", `${20}`);
    params.set("sort", "id");
    params.set("desc", `${true}`);
    if (modelSearch.length) params.set("freeText", modelSearch);
    if (project!.models?.length) params.set("excludeIds", project!.models.map(m => m.id).join(","));

    Models.getModels(params)
      .then(
        ({ data: modelsPages }) => {
          setModels((modelsPages as PagedResults<BdaModel>).content);
          setTotalModels((modelsPages as PagedResults<BdaModel>).pageMetadata.totalElements);
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('models-error', message, `${modelLabels.plural} Error ${status ? `(${status})` : ''}`, 'danger');
          setModels([]);
          // setError(true);
        }
      );
  }, [currentModelsPage, modelSearch, project, modelLabels, showToast]);

  const getServices = useCallback(() => {
    const params = new URLSearchParams();
    params.set("page", `${currentServicesPage}`);
    params.set("size", `${20}`);
    params.set("sort", "id");
    params.set("desc", `${true}`);
    if (serviceSearch.length) params.set("freeText", serviceSearch);
    if (project!.services?.length) params.set("excludeIds", project!.services.map(s => s.id).join(","));

    Services.getServices(params)
      .then(
        ({ data: servicesPages }) => {
          setServices((servicesPages as PagedResults<BdaService>).content);
          setTotalServices((servicesPages as PagedResults<BdaService>).pageMetadata.totalElements);
        },
        (err) => {
          showToast('services-error', `Could not get ${serviceLabels.plural}`, `${serviceLabels.plural} Error`, 'danger');
          setServices([]);
          setError(true);
        }
      );
  }, [currentServicesPage, serviceSearch, project, serviceLabels, showToast]);

  const getApplications = useCallback(() => {
    const params = new URLSearchParams();
    params.set("page", `${currentAppsPage}`);
    params.set("size", `${20}`);
    params.set("sort", "id");
    params.set("desc", `${true}`);
    if (appSearch.length) params.set("freeText", appSearch);
    if (project!.bdaApplications?.length) params.set("excludeIds", project!.bdaApplications.map(app => app.id).join(","));

    Applications.getApplications(params)
      .then(
        ({ data: appPages }) => {
          setApplications((appPages as PagedResults<BdaApplication>).content);
          setTotalApplications((appPages as PagedResults<BdaApplication>).pageMetadata.totalElements);
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('apps-error', message, `${applicationLabels.plural} Error ${status ? `(${status})` : ''}`, 'danger');
          setApplications([]);
        }
      );
  }, [currentAppsPage, appSearch, project, applicationLabels, showToast]);

  const handleRemoveModalClose = useCallback(() => {
    setShowRemoveModal(false);
    setAssetToRemove(null);
  }, []);

  const handleAssetToRemove = useCallback((asset: any, type: string) => (evt: React.MouseEvent<HTMLButtonElement>) => {
    evt.stopPropagation();
    setAssetToRemove({ id: asset.id, name: asset.name, type });
    setShowRemoveModal(true);
  }, []);

  const handleEditProject = useCallback(() => setShowEditModal(true), []);
  const handleEditModalClose = useCallback(() => {
    setShowEditModal(false);
    setValidated(false);
  }, []);

  const handleFieldChange = useCallback((field: keyof Project) => (evt: any) => {
    const value = field === "accessLevel" ? evt : evt.target.value;
    setNewProject(p => ({
      ...p!,
      [field]: value,
    }));
  }, []);

  const handleProjectSubmit = useCallback((evt: React.FormEvent<HTMLFormElement>) => {
    const form = evt.currentTarget;
    evt.stopPropagation();
    evt.preventDefault();
    setValidated(true);

    if (form.checkValidity() === true) {
      showLoader();

      const body = { ...newProject };
      delete body.bdaApplications;
      delete body.datasets;
      delete body.models;
      delete body.services;

      Projects.updateProject(body as Project)
        .then(
          ({ data }) => {
            hideLoader();
            showToast("projects-edit-success", "Project edited successfully", 'Project Success', 'success');
            setProject(proj => ({ ...proj!, name: data.name, description: data.description, accessLevel: data.accessLevel }));
            handleEditModalClose();
          },
          (err) => {
            hideLoader();
            const message = err.response?.data?.message || err.message;
            const status = err.response?.status;
            showToast(`projects-edit-error`, message, `Project Error ${status ? `(${status})` : ''}`, 'danger');
          }
        )
    }
  }, [showLoader, hideLoader, newProject, showToast, handleEditModalClose]);

  const addAsset = useCallback((asset: any, type: string) => () => {
    showLoader();

    const body = { id: asset.id, type };

    Projects.addAsset(+id!, body)
      .then(
        () => {
          hideLoader();
          showToast('asset-add-success', `Project updated, new ${type} added.`, "Project Success", 'success');
          refreshAssets();
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('asset-add-error', message, `Project Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [id, showLoader, hideLoader, showToast, refreshAssets]);

  const removeAsset = useCallback(() => {
    showLoader();

    Projects.removeAsset(+id!, assetToRemove)
      .then(
        () => {
          hideLoader();
          showToast('asset-remove-success', `Project updated, ${assetToRemove.type} removed`, "Project Success", 'success');
          refreshAssets();
          handleRemoveModalClose();
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('asset-remove-error', message, `Project Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [showLoader, id, assetToRemove, hideLoader, showToast, refreshAssets, handleRemoveModalClose]);

  const changePage = useCallback((asset: 'applications' | 'datasets' | 'models' | 'services') => (page: number) => {
    if (asset === "applications") setCurrentAppsPage(page);
    else if (asset === "datasets") setCurrentDatasetsPage(page);
    else if (asset === "services") setCurrentServicesPage(page);
    else setCurrentModelsPage(page);
  }, []);

  const renderAssets = useCallback(() => {
    return (
      <Card className="assets-card">
        <Card.Body className="p-0">
          <Tabs
            centered
            defaultActiveKey="applications"
            items={[
              {
                key: "applications",
                label: (
                  <span>
                    <FontAwesomeIcon icon={solid('laptop-code')} />
                    <span className="ms-2">{applicationLabels.plural || "Applications"}</span>
                  </span>
                ),
                children: (
                  <AssetTab
                    data={applications as BdaApplication[]}
                    total={totalApplications}
                    currentPage={currentAppsPage}
                    assetType="application"
                    assetLabel={applicationLabels.singular}
                    onPageChange={changePage("applications")}
                    onSearchChange={debounceAppSearch}
                    onAdd={addAsset}
                  />
                )
              },
              {
                key: "datasets",
                label: (
                  <span>
                    <FontAwesomeIcon icon={solid('database')} />
                    <span className="ms-2">{datasetLabels.plural || "Datasets"}</span>
                  </span>
                ),
                children: (
                  <AssetTab
                    data={datasets as BdaDataset[]}
                    total={totalDatasets}
                    currentPage={currentDatasetsPage}
                    assetType="dataset"
                    assetLabel={datasetLabels.singular}
                    onPageChange={changePage("datasets")}
                    onSearchChange={debounceDatasetSearch}
                    onAdd={addAsset}
                  />
                )
              },
              {
                key: "models",
                label: (
                  <span>
                    <FontAwesomeIcon icon={solid('graduation-cap')} />
                    <span className="ms-2">{modelLabels.plural || "Models"}</span>
                  </span>
                ),
                children: (
                  <AssetTab
                    data={models as BdaModel[]}
                    total={totalModels}
                    currentPage={currentModelsPage}
                    assetType="model"
                    assetLabel={modelLabels.singular}
                    onPageChange={changePage("models")}
                    onSearchChange={debounceModelSearch}
                    onAdd={addAsset}
                  />
                )
              },
              {
                key: "services",
                label: (
                  <span>
                    <FontAwesomeIcon icon={solid('cubes')} />
                    <span className="ms-2">{serviceLabels.plural || "Services"}</span>
                  </span>
                ),
                children: (
                  <AssetTab
                    data={services as BdaService[]}
                    total={totalServices}
                    currentPage={currentServicesPage}
                    assetType="service"
                    assetLabel={serviceLabels.singular}
                    onPageChange={changePage("services")}
                    onSearchChange={debounceServiceSearch}
                    onAdd={addAsset}
                  />
                )
              },
            ]}
          />
        </Card.Body>
      </Card>
    )
  }, [
    applicationLabels, datasetLabels, modelLabels, serviceLabels, addAsset, applications, datasets, models, services, changePage,
    currentAppsPage, currentDatasetsPage, currentModelsPage, currentServicesPage, debounceAppSearch, debounceDatasetSearch,
    debounceModelSearch, debounceServiceSearch, totalApplications, totalDatasets, totalModels, totalServices,
  ]);

  const renderProjectAssets = useCallback(() => {
    if (loading) return <AppLoader className="py-5" justify="center" size={60} />;

    if (
      project!.bdaApplications?.length === 0 && project!.datasets?.length === 0 &&
      project!.models?.length === 0 && project!.services?.length === 0
    )
      return (
        <Alert className="py-2">
          <FontAwesomeIcon icon={solid('info-circle')} />
          <span className="ms-2">Project is empty. You can add new components from the <b>lists on the right</b>.</span>
        </Alert>
      );


    return (
      <>
        {project!.bdaApplications.map(app => (
          <Col md={6} key={app.id} className="mb-3">
            <ProjectAsset
              asset={app}
              type="application"
              actions={
                <Tooltip title="Remove">
                  <button className="circle-button" onClick={handleAssetToRemove(app, "application")}>
                    <FontAwesomeIcon icon={solid('times')} fixedWidth />
                  </button>
                </Tooltip>
              }
            />
          </Col>
        ))}
        {project!.datasets.map(dataset => (
          <Col md={6} key={dataset.id} className="mb-3">
            <ProjectAsset
              asset={dataset}
              type="dataset"
              actions={
                <Tooltip title="Remove">
                  <button className="circle-button" onClick={handleAssetToRemove(dataset, "dataset")}>
                    <FontAwesomeIcon icon={solid('times')} fixedWidth />
                  </button>
                </Tooltip>
              }
            />
          </Col>
        ))}
        {project!.models.map(model => (
          <Col md={6} key={model.id} className="mb-3">
            <ProjectAsset
              asset={model}
              type="model"
              actions={
                <Tooltip title="Remove">
                  <button className="circle-button" onClick={handleAssetToRemove(model, "model")}>
                    <FontAwesomeIcon icon={solid('times')} fixedWidth />
                  </button>
                </Tooltip>
              }
            />
          </Col>
        ))}
        {project!.services.map(service => (
          <Col md={6} key={service.id} className="mb-3">
            <ProjectAsset
              asset={service}
              type="service"
              actions={
                <Tooltip title="Remove">
                  <button className="circle-button" onClick={handleAssetToRemove(service, "service")}>
                    <FontAwesomeIcon icon={solid('times')} fixedWidth />
                  </button>
                </Tooltip>
              }
            />
          </Col>
        ))}
      </>
    )
  }, [loading, project, handleAssetToRemove]);

  const renderProject = useCallback(() => {
    const privacyIcon = project!.accessLevel === "PRIVATE" ? solid('user-secret') : project!.accessLevel === "TEAM" ? solid('users') : solid('globe-africa')

    return (
      <Card>
        <Card.Header className="p-2 d-flex">
          <div className="p-3 rounded bg-project">
            <div className="position-relative text-white">
              <FontAwesomeIcon icon={solid("folder")} className="align-self-center" size="2x" />
            </div>
          </div>
          <Col className="px-3 align-self-center">
            <h4 className="mb-0">{project!.name}</h4>
            <p className="mb-0">{project!.description}</p>
          </Col>
          <Col md={2} className="text-end">
            <Badge className="w-auto shadow">
              <FontAwesomeIcon icon={privacyIcon} />
              <span className="ms-2">{project!.accessLevel}</span>
            </Badge>
          </Col>
          <Col md={1} className="border-start ms-3 text-center align-self-center">
            <Tooltip title="Edit">
              <button className="circle-button dark" onClick={handleEditProject}>
                <FontAwesomeIcon icon={solid('pen')} />
              </button>
            </Tooltip>
          </Col>
        </Card.Header>
        <Card.Body>
          <Row className="g-0">{renderProjectAssets()}</Row>
        </Card.Body>
      </Card>
    )
  }, [project, renderProjectAssets, handleEditProject]);

  const renderAsyncContent = useCallback(() => {
    if (error) return (
      <Alert className="py-2 m-0" variant="danger">
        <FontAwesomeIcon icon={solid('exclamation-triangle')} />
        <span className="ms-2">An error occurred, please try again later.</span>
      </Alert>
    )

    if (!project) return (
      <Flex justify="center" className="py-5">
        <AlidaLoader size={300} />
      </Flex>
    )

    return (
      <>
        <Breadcrumb>
          <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/projects' }}>Projects</Breadcrumb.Item>
          <Breadcrumb.Item active>{project.name}</Breadcrumb.Item>
        </Breadcrumb>

        <Row className="gx-2">
          <Col className="mb-3 mb-md-0" md={6}>{renderProject()}</Col>
          <Col md={6}>{renderAssets()}</Col>
        </Row>
      </>
    );
  }, [error, project, renderProject, renderAssets]);

  return (
    <Container fluid className="project-edit my-2">
      <Helmet>
        <title>Project {id} Management</title>
      </Helmet>

      {renderAsyncContent()}

      <Modal show={showRemoveModal} onHide={handleRemoveModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>Remove {assetToRemove?.type}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to remove {assetToRemove?.type} <b>{assetToRemove?.name}</b> from this group?</p>

          <Alert className="py-2 m-0">
            <FontAwesomeIcon icon={solid('info-circle')} />
            <span className="ms-2">The {assetToRemove?.type} will be again among the available ones.</span>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" size="sm" className="border" onClick={handleRemoveModalClose}>Close</Button>
          <Button variant="danger" size="sm" onClick={removeAsset}>Remove</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showEditModal} onHide={handleEditModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Project</Modal.Title>
        </Modal.Header>
        <Form noValidate validated={validated} onSubmit={handleProjectSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3" controlId="project-name">
              <Form.Label>Name</Form.Label>
              <Form.Control size="sm" placeholder="Project Name" value={newProject?.name} onChange={handleFieldChange("name")} required />
              <Form.Control.Feedback type="invalid">Project Name is required.</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-3" controlId="project-description">
              <Form.Label>Description</Form.Label>
              <Form.Control as="textarea" rows={3} size="sm" placeholder="Project Description" value={newProject?.description} onChange={handleFieldChange("description")} />
            </Form.Group>
            <Form.Group className="mb-3" controlId="project-access-level">
              <Form.Label>Privacy Level</Form.Label>
              <Select
                allowClear
                placeholder="Select privacy level"
                value={newProject?.accessLevel}
                onChange={handleFieldChange('accessLevel')}
                className="w-100"
              >
                {accessLevelOptions
                  .filter(option => window.__env.ALLOWED_ACCESS_LEVELS ? window.__env.ALLOWED_ACCESS_LEVELS.includes(option.value) : true)
                  .map((option, index) => (
                    <Select.Option key={index} value={option.value} label={option.label}>
                      <div>
                        <FontAwesomeIcon icon={option.icon} fixedWidth />
                        <span className="ms-2">{option.label}</span>
                      </div>
                    </Select.Option>
                  ))}
              </Select>
              {validated && !newProject?.accessLevel && <Form.Control.Feedback className="d-block" type="invalid">Project Privacy Level is required.</Form.Control.Feedback>}
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="light" size="sm" className="border" onClick={handleEditModalClose}>Close</Button>
            <Button variant="success" size="sm" type="submit">Confirm</Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  )
}

type TabProps = {
  data: BdaApplication[] | BdaDataset[] | BdaModel[] | BdaService[];
  total?: number;
  currentPage: number;
  assetType: 'application' | 'dataset' | 'model' | 'service';
  assetLabel?: string;
  onPageChange: (page: number, size: number) => void;
  onAdd: (item: any, type: string) => React.MouseEventHandler<HTMLButtonElement>;
  onSearchChange: (value: string) => void;
}

const AssetTab = memo(({ data, total, currentPage, assetType, assetLabel = "Asset", onPageChange, onAdd, onSearchChange }: TabProps) => {

  const renderDataItem = useCallback((item: BdaApplication | BdaDataset | BdaModel | BdaService) => {
    if (assetType === "application" && setObjectType<BdaApplication>(item))
      return (
        <BdaApplicationItem
          application={item}
          projectMode={true}
          actions={
            <Tooltip title="Add">
              <button className="circle-button" onClick={onAdd(item, assetType)}>
                <FontAwesomeIcon icon={solid('plus')} fixedWidth />
              </button>
            </Tooltip>
          }
        />
      );
    else if (assetType === "dataset" || assetType === "model")
      return (
        <AssetItem
          asset={item}
          type={assetType}
          actions={
            <Tooltip title="Add">
              <button className="circle-button" onClick={onAdd(item, assetType)}>
                <FontAwesomeIcon icon={solid('plus')} fixedWidth />
              </button>
            </Tooltip>
          }
        />
      );
    else return setObjectType<BdaService>(item) && (
      <ServiceItem
        service={item}
        actions={
          <Tooltip title="Add">
            <button className="circle-button" onClick={onAdd(item, assetType)}>
              <FontAwesomeIcon icon={solid('plus')} fixedWidth />
            </button>
          </Tooltip>
        }
      />
    );
  }, [assetType, onAdd]);

  const renderData = useCallback(() => {
    if (!data) return <AppLoader className="py-5 my-5" justify="center" size={60} />;

    return data.length === 0
      ? (
        <Alert className="py-2 m-2">
          <FontAwesomeIcon icon={solid('info-circle')} />
          <span className="ms-2">No {assetType}s found.</span>
        </Alert>
      )
      : (
        <Row className="assets-wrapper p-2 mx-0 gx-3">
          {data.map(item => (
            <Col md={6} key={item.id} className="mb-3">{renderDataItem(item)}</Col>
          ))}
        </Row>
      )
  }, [data, renderDataItem, assetType]);

  return (
    <>
      <Flex justify="space-between" align="center" className="py-1 px-2 border-bottom">
        <Col className="pe-4">
          <AppSearch placeholder={`Search ${assetLabel}...`} onChange={onSearchChange} />
        </Col>

        <Pagination
          simple
          showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
          total={total}
          pageSize={20}
          current={currentPage}
          onChange={onPageChange}
          className="d-flex align-items-center"
        />
      </Flex>
      {renderData()}
    </>
  )
})